import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready. Forces hydration within 1500ms via the requestIdleCallback
 * timeout option, preventing indefinite delays on busy pages.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    // timeout: 1500 ensures hydration runs within 1.5s even if the browser
    // is never idle (e.g. busy Shopify storefronts with heavy 3rd-party scripts).
    requestIdleCallback(() => hydrateSection(sectionId, url), { timeout: 1500 });
  });
}

/**
 * Triggers hydration immediately on the first pointerenter of a given element,
 * bypassing the idle-callback delay entirely when a user is already interacting
 * with the page. Falls back to the normal idle-callback hydration regardless.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {Element | null} triggerElement - Element whose first pointerenter triggers early hydration
 * @param {URL} [url] - The URL to render the section from
 */
export function hydrateOnInteraction(sectionId, triggerElement, url) {
  if (!triggerElement) return;
  triggerElement.addEventListener(
    'pointerenter',
    () => hydrateSection(sectionId, url),
    { once: true, passive: true }
  );
}
